import { SupportedLanguage } from '@/lib/types';
import { appStateService } from '@/state/app.state.service';

import { defaultLocale } from './config';
import axios from 'axios';
import _merge from 'lodash/merge';
import { getEnvVariable } from '@/lib/getEnvVariable';
import { retryChunkImport } from '@/lib/retryChunkImport';
import { isUndefined } from '@/lib/variable';
import { convertMustachePlaceholders } from './utils';

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Axios waits forever by default, and both calls below sit on the path that gates
 * the whole page: a request that stalls rather than fails leaves the player on a
 * spinner with nothing thrown to fall back from. `getAvailableLocalesFromAPI` in
 * the server twin already bounds the same endpoint at three seconds.
 */
const TRANSLATION_REQUEST_TIMEOUT_MS = 3000;

/**
 * Three chunk requests on the critical path, and the only one of these loads
 * with nothing behind it: English is what every other branch falls back to, so
 * a dropped request here rejects before there is anything to fall back on and
 * takes the render with it. Retried for that reason — on the mobile networks
 * these landings arrive on the usual cause is a moment of lost connectivity,
 * which a second attempt rides over.
 */
async function loadDefaultMessages(): Promise<any> {
  const [baseModule, overridesModule] = await Promise.all([
    retryChunkImport(() => import('@web-store-platforms/locales/en/common.json')),
    retryChunkImport(() => import('../../locales/en.overrides.json')),
  ]);
  const { deepMerge } = await retryChunkImport(() => import('@web-store-platforms/locales'));

  return convertMustachePlaceholders(deepMerge(baseModule.default, overridesModule.default));
}

function getCachedTranslations(locale: string): any | null {
  if (typeof window === 'undefined') return null;

  try {
    const key = `translations_${locale}`;
    const timestampKey = `translations_${locale}_timestamp`;

    const timestamp = localStorage.getItem(timestampKey);
    const cached = localStorage.getItem(key);

    if (!timestamp || !cached) return null;

    const now = Date.now();
    const cacheTime = parseInt(timestamp);

    if (now - cacheTime > CACHE_DURATION) {
      localStorage.removeItem(key);
      localStorage.removeItem(timestampKey);

      return null;
    }

    return JSON.parse(cached);
  } catch (error) {
    console.warn('Failed to load cached translations:', error);

    return null;
  }
}

function storeTranslations(locale: string, messages: any): void {
  if (typeof window === 'undefined') return;

  try {
    const key = `translations_${locale}`;
    const timestampKey = `translations_${locale}_timestamp`;

    localStorage.setItem(key, JSON.stringify(messages));
    localStorage.setItem(timestampKey, Date.now().toString());
  } catch (error) {
    console.warn('Failed to store translations in localStorage:', error);
  }
}

export function clearTranslationCache(): void {
  if (typeof window === 'undefined') return;

  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith('translations_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear translation cache:', error);
  }
}

async function loadAvailableLocales(publisherId: string): Promise<SupportedLanguage[]> {
  if (!publisherId || isUndefined(publisherId)) {
    return [defaultLocale];
  }

  try {
    const baseURL =
      getEnvVariable('NEXT_PUBLIC_REACT_APP_API_BASE_URL', process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL) || 'https://api-store-dev.appcharge.com';
    const url = `${baseURL}/store/translations/${publisherId}/languages`;
    const response = await axios.get(url, { timeout: TRANSLATION_REQUEST_TIMEOUT_MS });

    return response.data || [];
  } catch (error) {
    console.warn('Failed to load available locales:', error);

    return [defaultLocale];
  }
}

async function loadApiMessages(
  locale: string,
  publisherId: string,
  availableLocales: SupportedLanguage[]
): Promise<any> {
  if (!publisherId || isUndefined(publisherId)) {
    return {};
  }

  try {
    const localeData = availableLocales.find((loc) => loc.isoCode === locale);
    const languageName = localeData?.language || locale;

    const baseURL =
      process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL || 'https://api-store-dev.appcharge.com';
    const response = await axios.get(
      `${baseURL}/store/translations/${publisherId}/languages/${languageName}`,
      { timeout: TRANSLATION_REQUEST_TIMEOUT_MS }
    );

    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid translation response format');
    }

    if (Object.keys(response.data).length === 0) {
      throw new Error('Empty translation data received');
    }

    return convertMustachePlaceholders(response.data);
  } catch (error) {
    console.warn(`Failed to load translations for ${locale}:`, error);
    console.error(`🌍 [next-intl-client] Error details:`, {
      locale,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
}

export async function getAvailableLanguages(publisherId: string): Promise<SupportedLanguage[]> {
  if (!publisherId || isUndefined(publisherId)) {
    return [defaultLocale];
  }

  const existingLanguages = new Set<string>();

  try {
    const availableLocales = await loadAvailableLocales(publisherId);

    const filteredAvailableLocales = availableLocales.filter((item: SupportedLanguage) => {
      if (existingLanguages.has(item.language)) {
        return false;
      }
      existingLanguages.add(item.language);

      return true;
    });

    appStateService.setLanguagesList(filteredAvailableLocales);

    return filteredAvailableLocales;
  } catch (error) {
    console.warn('Failed to load available locales', error);

    return [defaultLocale];
  }
}

export async function getMessagesForLocale(locale: string, publisherId: string) {
  let messages: any;
  const defaultMessages = await loadDefaultMessages();

  try {
    if (locale === defaultLocale.isoCode) {
      messages = defaultMessages;
    } else {
      const cachedMessages = getCachedTranslations(locale);
      if (cachedMessages) {
        // Merge cached messages with default as fallback for missing keys.
        // Cached messages written before the placeholder normalization was
        // introduced may still contain `{{var}}`, so normalize on read too.
        messages = _merge({}, defaultMessages, convertMustachePlaceholders(cachedMessages));
      } else {
        try {
          const availableLocales = await loadAvailableLocales(publisherId);
          const apiMessages = await loadApiMessages(locale, publisherId, availableLocales);

          // Merge API messages with default as fallback for missing keys
          messages = _merge({}, defaultMessages, apiMessages);
          storeTranslations(locale, messages);
        } catch (error) {
          console.warn(`Could not load translations for ${locale}, falling back to English`);
          messages = defaultMessages;
        }
      }
    }
  } catch (error) {
    console.error(`Error loading messages for ${locale}:`, error);
    console.warn('All translation loading failed, falling back to English');
    messages = defaultMessages;
  }

  return messages;
}
